// ============================================================================
// Postes : ordre d'affichage et couleurs (M9-2)
// ============================================================================
// Demande du boss : « que les pharmaciens apparaissent en haut et en bleu foncé
// (dans l'emploi du temps), puis les préparatrices dans un bleu un peu plus
// clair, puis les étudiants en vert clair, puis les autres postes en marron
// clair. Ça permettra de visuellement marquer la différence entre les postes. »
//
// Le rang et la couleur vivent ensemble ici pour qu'ils ne puissent pas diverger :
// deux postes de même couleur doivent toujours être voisins à l'écran.
// ============================================================================
import type { EmployeePosition } from '../types';

/**
 * Ordre d'affichage. Il reprend celui de l'énumération PostgreSQL (l'API trie
 * déjà `position: 'asc'`), mais on le redéclare ici pour que le regroupement
 * visuel ne dépende pas d'un détail de la base.
 */
export const POSITION_RANK: Record<EmployeePosition, number> = {
  PHARMACIEN: 0,
  ETUDIANT_REMPLACANT_PHARMACIEN: 1,
  PREPARATEUR: 2,
  ETUDIANT: 3,
  MAGASINIER: 4,
  MENAGE: 5,
  AUTRE: 6,
};

export interface PositionStyle {
  /** Fond de la case « présent ». */
  bg: string;
  /** Couleur du texte, choisie pour rester lisible sur `bg`. */
  text: string;
  /** Libellé du groupe, tel qu'il apparaît dans la légende. */
  group: string;
}

/**
 * Un étudiant REMPLAÇANT PHARMACIEN reste dans le bleu des pharmaciens (nuance
 * un peu plus claire) : c'est déjà ainsi que le moteur de couverture le compte
 * — le ranger avec les étudiants contredirait les alertes d'effectif.
 */
const PHARMACIEN_STYLE: PositionStyle = { bg: '#1e3a5f', text: '#ffffff', group: 'Pharmacien' };
const REMPLACANT_STYLE: PositionStyle = { bg: '#2b5480', text: '#ffffff', group: 'Étudiant remplaçant pharmacien' };
const PREPARATEUR_STYLE: PositionStyle = { bg: '#4a80b4', text: '#ffffff', group: 'Préparateur' };
const ETUDIANT_STYLE: PositionStyle = { bg: '#9ed8ac', text: '#14532d', group: 'Étudiant' };
const AUTRE_STYLE: PositionStyle = { bg: '#cbb094', text: '#4a331f', group: 'Autres postes' };

const POSITION_STYLES: Record<EmployeePosition, PositionStyle> = {
  PHARMACIEN: PHARMACIEN_STYLE,
  ETUDIANT_REMPLACANT_PHARMACIEN: REMPLACANT_STYLE,
  PREPARATEUR: PREPARATEUR_STYLE,
  ETUDIANT: ETUDIANT_STYLE,
  MAGASINIER: AUTRE_STYLE,
  MENAGE: AUTRE_STYLE,
  AUTRE: AUTRE_STYLE,
};

/** Couleurs d'un poste. Repli sur « autres postes » si le poste est inconnu. */
export function positionStyle(position: EmployeePosition | undefined | null): PositionStyle {
  return (position && POSITION_STYLES[position]) || AUTRE_STYLE;
}

/** Tri d'affichage : par poste, puis par nom de famille. */
export function byPositionThenName<T extends { position: EmployeePosition; lastName: string; firstName: string }>(
  a: T,
  b: T
): number {
  const ra = POSITION_RANK[a.position] ?? 99;
  const rb = POSITION_RANK[b.position] ?? 99;
  if (ra !== rb) return ra - rb;
  const last = a.lastName.localeCompare(b.lastName, 'fr');
  return last !== 0 ? last : a.firstName.localeCompare(b.firstName, 'fr');
}

/** Les quatre groupes de la légende, dans l'ordre d'affichage. */
export const POSITION_LEGEND: PositionStyle[] = [
  PHARMACIEN_STYLE,
  PREPARATEUR_STYLE,
  ETUDIANT_STYLE,
  AUTRE_STYLE,
];
